import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import { SearchInput } from 'shouldve-been-mui';
import type Vessel from '../../Models/VesselModel';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import type { SettingsContextProps } from '../../Contexts/SettingsContext';
import SettingsContext from '../../Contexts/SettingsContext';
import { getTableRowBackgroundColor } from '../../Utils/TableRowsColor';
import { updateLastActivity } from '../../Utils/LastActivity';

type VesselFilteringDialogProps = {
    controller: boolean,
    onClose: (success: boolean) => void,
    vessels: Vessel[],
    initialSelectedVesselIds: number[],
};

function VesselFilteringDialog(props: Readonly<VesselFilteringDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const settingsContext: SettingsContextProps = useContext<SettingsContextProps>(SettingsContext);

    const [selectedVesselIds, setSelectedVesselIds] = useState<number[]>([]);
    const [query, setQuery] = useState<string>('');
    const [rerenderFlag, setRerenderFlag] = useState<Date>(new Date());
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const toggleRowSelected: (vesselId: number) => void = useCallback((vesselId: number) => {

        if (selectedVesselIds.includes(vesselId)) { //already selected
            const newSelectedVesselIds: number[] = selectedVesselIds.filter((currentId: number) => currentId !== vesselId);
            setSelectedVesselIds(newSelectedVesselIds);
        } else {
            const  newSelectedVesselIds: number[] = [...selectedVesselIds];
            newSelectedVesselIds.push(vesselId);
            setSelectedVesselIds(newSelectedVesselIds);
        }
    }, [selectedVesselIds]);

    const tableRows: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const filteredVessels: Vessel[] = applySearchFilter(props.vessels, query, selectedVesselIds);

        return filteredVessels.map((vessel: Vessel, index: number) => {
            return (
                <TableRow
                    key={vessel.vesselId}
                    onClick={(): void => toggleRowSelected(vessel.vesselId)}
                    style={{
                        backgroundColor: getTableRowBackgroundColor(index, selectedVesselIds.includes(vessel.vesselId), false, settingsContext.theme),
                    }}    
                >
                    <TableCell>
                        {vessel.imoNumber}
                    </TableCell>
                    <TableCell>
                        {vessel.vesselName}
                    </TableCell>
                </TableRow>
            );
        });
    }, [props.vessels, query, selectedVesselIds, settingsContext.theme, toggleRowSelected]);

    useEffect(() => {
    
        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        setSelectedVesselIds(props.initialSelectedVesselIds);
    }, [props.initialSelectedVesselIds, rerenderFlag]);

    function applySearchFilter(vessels: Vessel[], currentQuery: string, currentSelectedVesselIds: number[]): Vessel[] {

        const activeVessels: Vessel[] = vessels.filter((vessel: Vessel) => vessel.status === '-');

        if (currentQuery === '') {
            return activeVessels;
        }

        return activeVessels.filter((vessel: Vessel) => (vessel.imoNumber?.toLowerCase().includes(currentQuery.toLowerCase()) ?? false) || vessel.vesselName.toLowerCase().includes(currentQuery.toLowerCase()) || currentSelectedVesselIds.includes(vessel.vesselId));
    }

    function handleSelect(): void {

        localStorageContext.localStorageService.setOrderVesselFiltering(selectedVesselIds);

        handleClose(true);
    }

    function handleClear(): void {

        localStorageContext.localStorageService.setOrderVesselFiltering([]);

        handleClose(true);
    }

    function handleClose(success: boolean): void {
        
        updateLastActivity(userContext, localStorageContext);

        setSelectedVesselIds([]);
     
        setQuery('');

        props.onClose(success);
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => handleClose(false)}
            scroll='paper'
            maxWidth='xl'
            PaperProps={{
                sx: {
                    position: 'fixed',
                    top: 10,
                },
            }}
            TransitionProps={{
                onEntered: (): void => setRerenderFlag(new Date()),
            }}
        >
            <DialogTitle>Select Vessels</DialogTitle>
            <DialogContent>
                <SearchInput 
                    value={query}
                    onChange={setQuery}
                    dense={true}
                    minWidth={Math.min(windowSize[0] * 0.8, 500)}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                />
                <br />
                <br />
                <TableContainer style={{
                    maxHeight: '550px',
                    minWidth: Math.min(windowSize[0] * 0.8, 500),
                    maxWidth: Math.min(windowSize[0] * 0.8, 500),
                }}>
                    <Table 
                        size='small'
                        stickyHeader
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    IMO Number
                                </TableCell>
                                <TableCell>
                                    Vessel Name
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableRows}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={(): void => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        minWidth: '100px',
                        maxWidth: '100px',
                        minHeight: '32px',
                        maxHeight: '32px',
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleClear}
                    color='secondary'
                    variant='contained'
                    style={{
                        minWidth: '100px',
                        maxWidth: '100px',
                        minHeight: '32px',
                        maxHeight: '32px',
                    }}
                >
                    Clear
                </Button>
                <Button 
                    onClick={handleSelect}
                    color='primary'
                    variant='contained'
                    style={{
                        minWidth: '100px',
                        maxWidth: '100px',
                        minHeight: '32px',
                        maxHeight: '32px',
                    }}
                >
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default memo(VesselFilteringDialog);
